import { Coins, PlusCircle, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

interface CreditBalanceViewProps {
  showTooltip: boolean;
  included: number;
  used: number;
  maxLimit: number | null;
  setShowTooltip: (show: boolean) => void;
}

export const CreditBalanceView = ({
  showTooltip,
  included,
  used,
  maxLimit,
  setShowTooltip,
}: CreditBalanceViewProps) => {
  const allowOverusage = maxLimit === null;
  const cap = maxLimit ?? included;
  const percentage = cap === 0 ? 100 : Math.min(Math.round((used / cap) * 100), 100);

  // Color logic:
  // - Warning tone if overusage is not allowed and >= 90% used
  // - Green otherwise
  const shouldShowOrange = !allowOverusage && percentage >= 90;
  const exceedsLimit = used > included;

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        className={`flex cursor-default items-center gap-3 rounded-full border px-4 py-1.5 shadow-sm transition-all ${
          shouldShowOrange
            ? "border-warning-border bg-warning-subtle"
            : "border-success-border bg-success-subtle"
        }`}
      >
        <div
          className={`rounded-full p-1 ${shouldShowOrange ? "bg-warning-subtle-strong" : "bg-success-subtle-strong"}`}
        >
          <Coins size={14} className={shouldShowOrange ? "text-warning" : "text-success"} />
        </div>

        <div className="flex flex-col -space-y-0.5">
          <span className="text-2xs font-semibold uppercase tracking-micro text-text-disabled">
            Credits
          </span>
          <div className="flex items-center gap-1.5">
            <span
              className={`text-sm font-bold tabular-nums ${
                allowOverusage
                  ? "text-black"
                  : shouldShowOrange
                    ? "text-warning-text"
                    : "text-success-text"
              }`}
            >
              {used.toLocaleString()}
            </span>
            {exceedsLimit || included === 0 ? (
              <span className="text-xs font-bold text-text-disabled">used</span>
            ) : (
              <span className="text-2xs font-bold tabular-nums text-text-disabled">
                / {included.toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {/* Mini progress bar inside the pill */}
        <div className="h-1.5 w-12 overflow-hidden rounded-full bg-border/50">
          <div
            className={`h-full shadow-sm transition-all duration-300 ease-out ${
              shouldShowOrange ? "bg-warning" : "bg-success"
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {showTooltip && (
        <div className="absolute right-0 top-full z-popover mt-3 w-64 rounded-2xl border border-border-subtle bg-card/95 p-4 shadow-[0_20px_50px_rgb(0,0,0,0.08)] backdrop-blur-md animate-in fade-in slide-in-from-top-2">
          <div className="mb-4 flex items-center gap-3 border-b border-border-subtle pb-4">
            <div
              className={`rounded-xl p-2 ${shouldShowOrange ? "bg-warning-subtle" : "bg-success-subtle"}`}
            >
              <TrendingUp
                className={`h-5 w-5 ${shouldShowOrange ? "text-warning" : "text-success"}`}
              />
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground">Usage Overview</h4>
              <p className="text-xs font-medium text-muted-foreground">
                {shouldShowOrange
                  ? "Running low"
                  : allowOverusage
                    ? "Overusage allowed"
                    : "Healthy balance"}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm font-medium">
              <span className="text-muted-foreground">Total Allocated</span>
              <span className="tabular-nums text-muted-foreground">
                {included.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm font-medium">
              <span className="text-muted-foreground">Credits Used</span>
              <span className="tabular-nums text-muted-foreground">{used.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between border-t border-border-subtle pt-3 text-sm font-bold">
              <span className="text-muted-foreground">Overusage Allowed?</span>
              <span className="text-black">{allowOverusage ? "Yes" : "No"}</span>
            </div>
          </div>

          {/* Hide upgrade plan until it's implemented */}
          {false && shouldShowOrange && (
            <Link
              to="/subscription"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-xs font-bold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-press"
            >
              <PlusCircle size={14} />
              Upgrade Plan
            </Link>
          )}

          <div className="absolute -top-1.5 right-6 h-3 w-3 rotate-45 border-l border-t border-border-subtle bg-card"></div>
        </div>
      )}
    </div>
  );
};
